import React, { FC, useState } from 'react';
import { styled, css } from 'styled-components';

import Row from 'components/common/Row';
import Text from 'components/common/Text';
import { DeliveryState } from 'types/api';
import { DeliveryParam } from '../graphQL/types';
import CommonImage from 'components/common/CommonImage';
import { ICON_ARROW_GRAY_BOLD, ICON_CHECKMARK } from 'assets/icons';
import Divider from 'components/Divider';

const ButtonContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;

  width: 100%;

  overflow-x: auto;
  white-space: nowrap;

  &::-webkit-scrollbar {
    display: none;
  }

  ${props =>
    props.$isMobile
      ? css`
          padding: 0 20px;
        `
      : css`
          flex-wrap: wrap;
          padding: 0 40px;
        `}
`;

const FilterButton = styled.button<{ $isSelected: boolean }>`
  display: inline-flex;
  padding: 10px 12px;
  white-space: nowrap;

  border-radius: 10px;
  background-color: ${props => (props.$isSelected ? '#414B56' : '#f7f7f8')};

  color: ${props => (props.$isSelected ? '#fff' : '#989BA2')};
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: ${props => (props.$isSelected ? 600 : 500)};
  line-height: 20px;
  letter-spacing: -0.6px;
`;

const SearchContainer = styled.div<{ $isFocused: boolean; $isSearch: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;

  padding: 12px;
  width: 100%;
  height: 44px;

  border-radius: 8px;
  border: ${props =>
    props.$isFocused ? '1.5px solid #414B56' : '1px solid #dbdcdf'};
  background: ${props => (props.$isSearch ? '#F7F7F8' : '#fff')};

  &:focus {
    border: 1.5px solid #414b56;
  }
`;

const DropDownContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  min-width: 66px;
  max-width: 66px;
  width: 100%;

  color: #989ba2;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: -0.6px;

  cursor: pointer;
`;

const DropDownContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  position: absolute;
  top: 60px;
  left: 0;
  padding: 16px 20px 20px 20px;
  width: 200px;

  border-radius: 12px;
  background: var(--Static-White, #fff);
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.15);
`;

const SearchInput = styled.input`
  width: 100%;
  border: none;
  outline: none;

  color: #1b1c1e;
  background: transparent;

  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
  letter-spacing: -0.056px;

  &::placeholder {
    color: #c2c4c8;
  }
`;

type SearchCategory =
  | 'phoneLike'
  | 'nameLike'
  | 'recipientNameLike'
  | 'addressLike'
  | 'customerIds'
  | 'orderIds'
  | 'ids';

const useFilterHooks = () => {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchCategory, setSearchCategory] =
    useState<SearchCategory>('phoneLike');
  const [isShowDropDowm, setIsShowDropDowm] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);

  const handleShowDropDown = () => {
    setIsShowDropDowm(prev => !prev);
  };

  const handleSearchKeywordChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchKeyword(e.target.value);
  };

  const handleResetSearch = () => {
    setSearchKeyword('');
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleSearchCategoryChange = (value: SearchCategory) => {
    setSearchCategory(value);
    handleShowDropDown();
  };

  return {
    searchKeyword,
    isShowDropDowm,
    searchCategory,
    isFocused,
    handleShowDropDown,
    handleSearchKeywordChange,
    handleSearchCategoryChange,
    handleFocus,
    handleBlur,
    handleResetSearch,
  };
};

export const DELIVERY_STATE_LIST = [
  { name: '결제완료', value: DeliveryState.PAID, color: '#0071E9' },
  { name: '배송준비', value: DeliveryState.PACKING, color: '#FF9200' },
  { name: '배송중', value: DeliveryState.ISSUED, color: '#32A06E' },
  { name: '배송완료', value: DeliveryState.COMPLETED, color: '#32A06E' },
  { name: '구매확정', value: DeliveryState.CONFIRMED, color: '#989BA2' },
  { name: '배송취소', value: DeliveryState.CANCELED, color: '#FF4242' },
  { name: '배송사고', value: DeliveryState.TROUBLED, color: '#FF4242' },
];

const DELIVERY_FILTER_LIST: { name: string; value: SearchCategory }[] = [
  {
    name: '전화번호',
    value: 'phoneLike',
  },
  {
    name: '주문자명',
    value: 'nameLike',
  },
  {
    name: '받는사람',
    value: 'recipientNameLike',
  },
  {
    name: '주소',
    value: 'addressLike',
  },
  {
    name: '고객 ID',
    value: 'customerIds',
  },
  {
    name: '주문 ID',
    value: 'orderIds',
  },
  {
    name: '배송 ID',
    value: 'ids',
  },
];

interface Props {
  isMobile: boolean;
  deliveryParam: DeliveryParam;
  isSearch: boolean;
  handleFilterClick: (value: DeliveryState | 'ALL') => void;
  handleAccpetSearchKeyword: (
    key: string,
    value: string,
    isAble: boolean,
  ) => void;
}

const FilterContainer: FC<Props> = props => {
  const {
    isMobile,
    deliveryParam,
    isSearch,
    handleFilterClick,
    handleAccpetSearchKeyword,
  } = props;

  const {
    searchKeyword,
    isShowDropDowm,
    searchCategory,
    isFocused,
    handleShowDropDown,
    handleSearchKeywordChange,
    handleSearchCategoryChange,
    handleFocus,
    handleBlur,
    handleResetSearch,
  } = useFilterHooks();

  const handleSearch = () => {
    if (isSearch) {
      handleResetSearch();
      handleAccpetSearchKeyword(searchCategory, '', !isSearch);

      return;
    }

    if (searchKeyword.trim() !== '') {
      handleAccpetSearchKeyword(searchCategory, searchKeyword, !isSearch);
    }
  };

  return (
    <>
      <ButtonContainer $isMobile={isMobile}>
        <FilterButton
          $isSelected={deliveryParam.filter.statusIn === null}
          onClick={() => handleFilterClick('ALL')}
        >
          전체
        </FilterButton>
        {DELIVERY_STATE_LIST.map(item => {
          return (
            <FilterButton
              key={item.value}
              $isSelected={deliveryParam.filter.statusIn?.includes(item.value)}
              onClick={() => handleFilterClick(item.value)}
            >
              {item.name}
            </FilterButton>
          );
        })}
      </ButtonContainer>
      <Row
        gap="24px"
        style={{
          position: 'sticky',
          top: isMobile ? '84px' : '100px',
          width: '100%',
          padding: isMobile ? '0 20px 24px' : '0 40px 24px',
          background: '#fff',
        }}
      >
        <SearchContainer $isFocused={isFocused} $isSearch={isSearch}>
          <DropDownContainer onClick={handleShowDropDown}>
            {
              DELIVERY_FILTER_LIST.find(item => item.value === searchCategory)
                .name
            }
            <CommonImage
              src={ICON_ARROW_GRAY_BOLD}
              style={{ rotate: isShowDropDowm ? '-90deg' : '90deg' }}
            />
          </DropDownContainer>
          {isShowDropDowm && (
            <DropDownContent>
              {DELIVERY_FILTER_LIST.map(item => {
                return (
                  <Row
                    key={item.value}
                    justifyContent="space-between"
                    style={{ width: '100%', cursor: 'pointer' }}
                    onClick={() => handleSearchCategoryChange(item.value)}
                  >
                    <Text
                      color={item.value === searchCategory ? '#06F' : '#46474C'}
                    >
                      {item.name}
                    </Text>
                    {item.value === searchCategory && (
                      <CommonImage src={ICON_CHECKMARK} />
                    )}
                  </Row>
                );
              })}
            </DropDownContent>
          )}
          <Divider
            style={{
              width: '1px',
              height: '16px',
              borderLeft: '1px solid #DBDCDF',
            }}
          />
          <SearchInput
            placeholder="검색어를 입력해 주세요."
            value={searchKeyword}
            onChange={handleSearchKeywordChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </SearchContainer>
        <Text
          color={searchKeyword.trim() !== '' ? '#414B56' : '#c2c4c8'}
          style={{ whiteSpace: 'nowrap', cursor: 'pointer' }}
          onClick={handleSearch}
        >
          {isSearch ? '취소' : '검색'}
        </Text>
      </Row>
    </>
  );
};

export default FilterContainer;
